/**
 * Pure rules shared by the web video-call components (#47).
 *
 * Kept free of React / Firebase / livekit-client imports so they run under
 * Vitest's node environment.
 */

/** How long the LiveKit room may take to connect after the token arrives. */
export const CONNECT_TIMEOUT_MS = 20_000;

/**
 * The LiveKit room name for a Namat room id. Every client (web clinic, web
 * doctor dashboard, mobile `formatRoomName`) must agree on this, otherwise the
 * two sides sit in different rooms and never meet.
 */
export function toLiveKitRoomName(roomId: string): string {
  return roomId.startsWith('coaching-') || roomId.startsWith('namat-')
    ? roomId
    : `namat-${roomId}`;
}

export interface CallOutcome {
  /** True only if the other party actually joined the LiveKit room. */
  connected: boolean;
  /** Minutes since the other party joined; 0 when they never did. */
  durationMinutes: number;
}

/**
 * What a finished call amounts to. Attendance must only be recorded when the
 * other party really joined — a failed attempt is not a 0-minute visit.
 */
export function callOutcome(remoteJoinedAtMs: number | null, nowMs: number): CallOutcome {
  if (remoteJoinedAtMs == null || !Number.isFinite(remoteJoinedAtMs)) {
    return { connected: false, durationMinutes: 0 };
  }
  return {
    connected: true,
    durationMinutes: Math.max(0, Math.round((nowMs - remoteJoinedAtMs) / 60000)),
  };
}

/**
 * The doctor records the beneficiary as attended only for a call that really
 * connected. A missing outcome (unknown) is treated as not connected.
 */
export function shouldRecordAttendance(outcome?: CallOutcome | null): boolean {
  return outcome?.connected === true;
}

/** `DisconnectReason` codes from livekit-client / @livekit/protocol. */
export const LK_DISCONNECT = {
  UNKNOWN_REASON: 0,
  CLIENT_INITIATED: 1,
  DUPLICATE_IDENTITY: 2,
  SERVER_SHUTDOWN: 3,
  PARTICIPANT_REMOVED: 4,
  ROOM_DELETED: 5,
  STATE_MISMATCH: 6,
  JOIN_FAILURE: 7,
  MIGRATION: 8,
  SIGNAL_CLOSE: 9,
  ROOM_CLOSED: 10,
  CONNECTION_TIMEOUT: 14,
  MEDIA_FAILURE: 15,
} as const;

export type CallErrorKind = 'token' | 'timeout' | 'connect' | 'disconnected';

/** A clear, user-facing message for a call failure (Arabic or English). */
export function callErrorMessage(
  kind: CallErrorKind,
  language: string,
  disconnectReason?: number
): string {
  const ar = language === 'ar';
  if (kind === 'token') {
    return ar
      ? 'تعذّر تجهيز المكالمة من الخادم. تحقق من اتصالك ثم أعد المحاولة.'
      : 'Could not prepare the call on the server. Check your connection and try again.';
  }
  if (kind === 'timeout') {
    return ar
      ? 'استغرق الاتصال بالمكالمة وقتاً أطول من المعتاد. تحقق من الإنترنت ثم أعد المحاولة.'
      : 'Connecting to the call took too long. Check your internet and try again.';
  }
  if (kind === 'connect') {
    return ar
      ? 'تعذّر الاتصال بخادم المكالمات. تحقق من الإنترنت ثم أعد المحاولة.'
      : 'Could not reach the call server. Check your internet and try again.';
  }
  switch (disconnectReason) {
    case LK_DISCONNECT.DUPLICATE_IDENTITY:
      return ar
        ? 'انقطعت المكالمة لأنك فتحتها من جهاز أو نافذة أخرى.'
        : 'The call was opened on another device or tab, so this one was disconnected.';
    case LK_DISCONNECT.ROOM_DELETED:
    case LK_DISCONNECT.ROOM_CLOSED:
    case LK_DISCONNECT.PARTICIPANT_REMOVED:
      return ar ? 'أُغلقت غرفة المكالمة.' : 'The call room was closed.';
    case LK_DISCONNECT.SERVER_SHUTDOWN:
    case LK_DISCONNECT.MIGRATION:
      return ar
        ? 'أُعيد تشغيل خادم المكالمات. أعد المحاولة.'
        : 'The call server restarted. Please try again.';
    case LK_DISCONNECT.JOIN_FAILURE:
    case LK_DISCONNECT.CONNECTION_TIMEOUT:
      return ar
        ? 'تعذّر الانضمام إلى المكالمة. تحقق من الإنترنت ثم أعد المحاولة.'
        : 'Could not join the call. Check your internet and try again.';
    case LK_DISCONNECT.MEDIA_FAILURE:
      return ar
        ? 'تعذّر تشغيل الصوت أو الصورة. تحقق من أذونات الكاميرا والميكروفون.'
        : 'Audio or video failed. Check camera and microphone permissions.';
    default:
      return ar
        ? 'انقطع الاتصال بالمكالمة. تحقق من الإنترنت ثم أعد المحاولة.'
        : 'The call connection was lost. Check your internet and try again.';
  }
}

/** Name of a DisconnectReason code, for logs and `callAttempts.error`. */
export function disconnectReasonName(reason: number | undefined): string {
  const entry = Object.entries(LK_DISCONNECT).find(([, code]) => code === reason);
  return entry ? entry[0] : `UNKNOWN(${reason ?? 'none'})`;
}
